/* ============================================================================
   hh-nav.js — behavior for the H&H global navigation.
   File: assets/hh-nav.js · v3 · 2026-07-27 · Owner: P33 (RT-176 batch 4)
   Design of record: P77 chrome-and-support implementation spec v2, sections 3 and 4,
   AS AMENDED BY A1 (HH_Project77_ImplementationSpec_ChromeAndSupport_A1_v1_20260727.md).

   v3 ADDS THE ONE THING A1-1 FOUND MISSING: a hover-intent delay. See section 1 below.
   Nothing else in this file changed.

   TWO SURFACES, DELIBERATELY DIFFERENT IN KIND.

   1. THE DESKTOP PANELS are native <details>/<summary> disclosures, so Shop, Brands and Support
      are fully keyboard operable and correctly announced WITH JAVASCRIPT OFF, and the browser
      supplies aria-expanded itself. The spec describes them as buttons carrying aria-expanded;
      a disclosure is that behavior plus a no-JS floor, so the element stands and this file adds
      only what a native disclosure does not give: hover to open, one panel at a time, Escape to
      close, and click or focus outside to close.

   2. THE DRAWER is ours end to end since v2. Dawn's MenuDrawer used to supply its mechanics, but
      Dawn's drawer is a drill-down and the owner ruled an accordion, so the markup no longer
      mirrors Dawn's and MenuDrawer no longer binds to it. Everything MenuDrawer gave has to be
      re-supplied here: focus moves in on open and back to the hamburger on close, focus is
      trapped while open, Escape closes, and the body cannot scroll behind it.

   THE DRAWER IS NEVER TOGGLED WITH THE `hidden` ATTRIBUTE. [hidden] is the weakest rule in the
   user-agent sheet and any display declaration beats it, which is what produced the
   reserved-nav-item defect on 2026-07-26. The toggle is the .is-open class; the mechanism is
   `visibility`, which also gives the exit animation an off state to animate to.
   ============================================================================ */
(function () {
  'use strict';

  /* ------------------------------------------------------------------ 1. desktop panels */
  var nav = document.querySelector('[data-hh-nav]');
  if (nav) {
    var discs = Array.prototype.slice.call(nav.querySelectorAll('[data-hh-disc]'));

    // A CLICK PINS. This is the one place the behavior needed thinking about rather than
    // transcribing. The spec asks for both "hover opens" and "click toggles", and on a mouse
    // the two collide: by the time the pointer reaches the label to click it, hover has already
    // opened the panel, so a plain toggle CLOSES it. Clicking "Shop" and watching the menu
    // shut is not a behavior anyone designed; it is what the two rules produce together.
    // So a click on an open panel PINS it instead: it stays open when the pointer leaves, and
    // the next click on the same label closes it. Click still toggles, from the customer's
    // point of view, and hover still opens.
    var pinned = null;

    // HOVER-INTENT DELAY (P77 chrome amendment A1-1). Hover used to open with no delay, so a
    // pointer traveling across the header to reach search or the cart opened Shop, then Brands,
    // then Support on the way past: three 400px panels flashing under a pointer that was never
    // going to the menu. The delay applies ONLY to the first panel of a visit. Once one is open,
    // moving to a sibling swaps immediately, because a delay on every hop makes browsing the nav
    // feel broken. 120ms is the window: below about 100ms it does not filter a traveling pointer,
    // above about 180ms a customer who meant to open the menu feels the lag.
    // The keyboard and click paths are NEVER delayed.
    var HOVER_INTENT_MS = 120;
    var hoverTimer = null;

    var cancelHoverIntent = function () {
      if (hoverTimer) { clearTimeout(hoverTimer); hoverTimer = null; }
    };
    var anyOpen = function () {
      return discs.filter(function (d) { return d.open; }).length > 0;
    };

    var closeDisc = function (disc) {
      disc.removeAttribute('open');
      if (pinned === disc) pinned = null;
    };
    var closeAllDiscs = function (except) {
      discs.forEach(function (d) { if (d !== except) closeDisc(d); });
    };
    var openDisc = function (disc) {
      cancelHoverIntent();
      closeAllDiscs(disc);
      if (!disc.open) disc.setAttribute('open', '');
    };

    // hover is bound only where hovering is real. A touch device synthesises mouseenter on tap
    // in some browsers, which would fight the click handler below for the same gesture.
    var canHover = !window.matchMedia || window.matchMedia('(hover: hover)').matches;

    discs.forEach(function (disc) {
      var summary = disc.querySelector('summary');
      if (summary) {
        // the toggle is driven here, not natively, so pinning is not racing the browser's own
        // open/close on the same click
        summary.addEventListener('click', function (event) {
          event.preventDefault();
          // A click CANCELS a pending hover-intent open and acts in the same frame. Without the
          // cancel, a click inside the 120ms window would open the panel and then the timer would
          // fire behind it and re-run closeAllDiscs, which clears the pin the click just set.
          cancelHoverIntent();
          if (pinned === disc) {
            closeDisc(disc);
          } else {
            openDisc(disc);
            pinned = disc;
          }
        });
      }

      // Hover is bound to the LIST ITEM rather than the disclosure, because the item is the
      // positioning context the panel is drawn inside: binding to the summary alone would close
      // the panel the moment the pointer travelled from the label down into what it just opened.
      var item = disc.closest('.hh-nav__item') || disc.parentElement;
      if (item && canHover) {
        item.addEventListener('mouseenter', function () {
          cancelHoverIntent();
          // Switching between siblings is immediate; opening the first panel waits out the
          // intent window. anyOpen() is read at ENTER time, which is the state the customer is
          // actually in: if a panel is up, they are browsing the nav and expect it to keep up.
          if (anyOpen()) {
            openDisc(disc);
          } else {
            hoverTimer = setTimeout(function () {
              hoverTimer = null;
              openDisc(disc);
            }, HOVER_INTENT_MS);
          }
        });
        item.addEventListener('mouseleave', function () {
          // A leave is a leave. There is no CLOSE delay, and there does not need to be: the panel
          // is drawn INSIDE the item, so the pointer cannot leave the item while traveling from
          // the label down into what it just opened.
          cancelHoverIntent();
          if (pinned !== disc) disc.removeAttribute('open');
        });
      }
    });

    // Escape closes and restores focus to the summary that opened the panel
    nav.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;
      cancelHoverIntent();
      var open = discs.filter(function (d) { return d.open; })[0];
      if (!open) return;
      closeDisc(open);
      var summary = open.querySelector('summary');
      if (summary) summary.focus();
      event.stopPropagation();
    });

    // RT-742 (P35 F-12, WCAG 1.4.13): Escape also closes a panel opened by HOVER, while the pointer
    // stays where it is and focus is outside the nav. The listener above only hears keys pressed
    // inside the nav. Focus is not moved: it was never in the menu. The panel does not reopen until
    // the pointer leaves the item and comes back, because only mouseenter opens it.
    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape' && event.key !== 'Esc') return;
      if (nav.contains(document.activeElement)) return;
      cancelHoverIntent();
      var open = discs.filter(function (d) { return d.open; })[0];
      if (!open) return;
      closeDisc(open);
    });

    // clicking or tabbing outside closes, and un-pins
    // A pending intent timer is cancelled too: a pointer that entered the nav and then clicked
    // elsewhere within 120ms must not have a panel appear behind the click.
    document.addEventListener('click', function (event) {
      if (!nav.contains(event.target)) { cancelHoverIntent(); pinned = null; closeAllDiscs(null); }
    });
    document.addEventListener('focusin', function (event) {
      if (!nav.contains(event.target)) { cancelHoverIntent(); pinned = null; closeAllDiscs(null); }
    });
  }

  /* ------------------------------------------------------------------- 2. the drawer */
  var drawer = document.querySelector('[data-hh-drawer]');
  var burger = document.querySelector('[data-hh-drawer-open]');
  if (!drawer || !burger) return;

  var panel = drawer.querySelector('.hh-drawer__panel');
  var closeBtn = drawer.querySelector('.hh-drawer__close');
  var FOCUSABLE = 'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])';

  function focusables() {
    // recomputed on every use: which rows are reachable changes as sections open and close
    return Array.prototype.slice.call(panel.querySelectorAll(FOCUSABLE)).filter(function (el) {
      return el.offsetParent !== null || el === closeBtn;
    });
  }

  function openDrawer() {
    drawer.classList.add('is-open');
    burger.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
    if (closeBtn) closeBtn.focus();
  }

  function closeDrawer() {
    drawer.classList.remove('is-open');
    burger.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
    burger.focus();
  }

  burger.addEventListener('click', function (event) {
    event.preventDefault();
    if (drawer.classList.contains('is-open')) closeDrawer(); else openDrawer();
  });

  Array.prototype.forEach.call(drawer.querySelectorAll('[data-hh-drawer-close]'), function (el) {
    el.addEventListener('click', function (event) {
      event.preventDefault();
      closeDrawer();
    });
  });

  drawer.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') {
      event.stopPropagation();
      closeDrawer();
      return;
    }
    if (event.key !== 'Tab') return;
    // focus trap: the drawer is aria-modal, so Tab must not reach the page underneath it
    var items = focusables();
    if (!items.length) return;
    var first = items[0];
    var last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  /* the accordion. Every section button toggles ITS OWN body only: sections are independent, so
     a customer can hold Shop open and glance at Support without losing their place. The arrival
     state is whatever the markup rendered (Shop open, the rest shut), read rather than assumed. */
  Array.prototype.forEach.call(drawer.querySelectorAll('[data-hh-dsec]'), function (btn) {
    btn.addEventListener('click', function () {
      var body = document.getElementById(btn.getAttribute('aria-controls'));
      if (!body) return;
      var willOpen = btn.getAttribute('aria-expanded') !== 'true';
      btn.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
      body.classList.toggle('is-open', willOpen);
    });
  });
})();
